import React, { useEffect, useState } from "react";
import { Link } from "./LocalLink";
import { NAV_ITEMS, RESOURCE_MENU_META } from "../mock";
import { LANGS } from "../translations";
import { useLanguage } from "../LanguageContext";
import { Menu, X, ChevronDown, ArrowUpRight, Globe } from "lucide-react";

const LANG_LABELS = { ES: "Español", EN: "English", PT: "Português" };

const Logo = ({ light }) => {
  const { lp } = useLanguage();
  return (
  <a href={lp("/#top")} className="flex items-center" data-testid="nav-home-link">
    <img src={light ? "/brainmart-logo.webp" : "/brainmart-logo-white.webp"} alt="Brainmart" width="144" height="64" className="h-16 w-auto" />
  </a>
  );
};

const LangToggle = ({ light }) => {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        data-testid="lang-selector-button"
        aria-label="Cambiar idioma"
        className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-[#173152] transition-colors hover:border-[#AECCF9] hover:bg-[#F8FAFC]"
      >
        <Globe className="h-3.5 w-3.5 text-[#316FD9]" />
        {lang}
        <ChevronDown className={`h-3.5 w-3.5 text-[#999999] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-2 w-36 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl shadow-[#173152]/10">
            {LANGS.map((l) => (
              <button
                key={l}
                data-testid={`lang-toggle-${l.toLowerCase()}`}
                onClick={() => { setLang(l); setOpen(false); }}
                className={`flex w-full items-center justify-between px-3.5 py-2 text-left text-xs transition-colors ${
                  lang === l ? "bg-[#F0F5FF] font-semibold text-[#316FD9]" : "text-[#666666] hover:bg-[#F8FAFC] hover:text-[#173152]"
                }`}
              >
                <span>{LANG_LABELS[l]}</span>
                <span className="text-[10px] font-semibold tracking-widest">{l}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const ResourcesDropdown = ({ label, light }) => {
  const { t } = useLanguage();
  const rm = t.resourcesMenu;
  const [open, setOpen] = useState(false);

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button className={`flex items-center gap-1 text-sm transition-colors ${light ? "text-[#173152]/80 hover:text-[#173152]" : "text-slate-300 hover:text-white"}`}>
        {label}
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      <div
        className={`absolute left-1/2 top-full z-50 w-[420px] -translate-x-1/2 pt-3 transition-all duration-200 ${
          open ? "visible opacity-100 translate-y-0" : "invisible opacity-0 -translate-y-1"
        }`}
      >
        <div className={`overflow-hidden rounded-2xl border p-2 shadow-2xl ${light ? "border-slate-200 bg-white" : "border-[#1e2637] bg-[#0b0f18]"}`}>
          <div className="p-2">
            {rm.items.map((item, i) => {
              const meta = RESOURCE_MENU_META[i];
              const Icon = meta.icon;
              return (
                <Link
                  key={item.title}
                  to={meta.href}
                  className={`group flex items-start gap-3.5 rounded-xl px-3 py-3 transition-colors ${light ? "hover:bg-[#F0F5FF]" : "hover:bg-[#131b2b]"}`}
                >
                  <span
                    className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${meta.color}1a`, border: `1px solid ${meta.color}33` }}
                  >
                    <Icon className="h-4 w-4" style={{ color: meta.color }} />
                  </span>
                  <span>
                    <span className={`flex items-center gap-1 text-sm font-semibold ${light ? "text-[#173152]" : "text-white"}`}>
                      {item.title}
                      <ArrowUpRight className={`h-3.5 w-3.5 transition-colors ${light ? "text-[#999999] group-hover:text-[#316FD9]" : "text-slate-500 group-hover:text-cyan-400"}`} />
                    </span>
                    <span className={`mt-0.5 block text-xs leading-relaxed ${light ? "text-[#666666]" : "text-slate-400"}`}>{item.desc}</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const MobileResources = ({ label, onNavigate, light }) => {
  const { t } = useLanguage();
  const rm = t.resourcesMenu;
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className={`flex w-full items-center justify-between text-sm ${light ? "text-[#173152]/80 hover:text-[#173152]" : "text-slate-300 hover:text-white"}`}
      >
        {label}
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className={`mt-3 space-y-1 border-l pl-3 ${light ? "border-slate-200" : "border-[#1e2637]"}`}>
          {rm.items.map((item, i) => {
            const Icon = RESOURCE_MENU_META[i].icon;
            return (
              <Link
                key={item.title}
                to={RESOURCE_MENU_META[i].href}
                onClick={onNavigate}
                className={`flex items-center gap-2.5 py-1.5 text-sm ${light ? "text-[#666666] hover:text-[#173152]" : "text-slate-400 hover:text-white"}`}
              >
                <Icon className="h-4 w-4" style={{ color: RESOURCE_MENU_META[i].color }} />
                {item.title}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function Navbar() {
  const { t, lp } = useLanguage();
  // Todo el sitio es tema claro: el navbar usa siempre la variante clara.
  const light = true;
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls = light ? "text-sm text-[#173152]/80 hover:text-[#173152] transition-colors" : "text-sm text-slate-300 hover:text-white transition-colors";
  const ctaCls = light
    ? "rounded-lg bg-[#316FD9] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#2558B5]"
    : "bm-gradient-btn rounded-full px-4 py-2 text-sm font-semibold text-white";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        light
          ? "bg-white/90 backdrop-blur-md border-b border-slate-200"
          : scrolled ? "bg-[#0a0e17]/90 backdrop-blur-md border-b border-[#1e2637]" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-16 items-center justify-between">
          <Logo light={light} />
          <nav className="hidden md:flex items-center gap-8">
            {NAV_ITEMS.map((link) => {
              if (link.type === "dropdown") return <ResourcesDropdown key={link.key} label={link.label || t.nav[link.key]} light={light} />;
              if (link.type === "route")
                return (
                  <Link key={link.key} to={link.to} className={linkCls}>
                    {link.label || t.nav[link.key]}
                  </Link>
                );
              return (
                <a key={link.key} href={lp(`/${link.href}`)} className={linkCls}>
                  {link.label || t.nav[link.key]}
                </a>
              );
            })}
          </nav>
          <div className="hidden md:flex items-center gap-4">
            <LangToggle light={light} />
            <Link to="/solicitar-demo" data-testid="nav-demo-button" className={ctaCls}>
              {t.nav.bookDemo}
            </Link>
          </div>
          <div className="flex items-center gap-3 md:hidden">
            <LangToggle light={light} />
            <button className={light ? "text-[#173152]" : "text-white"} onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-mobile-menu-toggle">
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div className={`md:hidden border-t backdrop-blur-md ${light ? "border-slate-200 bg-white/95" : "border-[#1e2637] bg-[#0a0e17]/95"}`}>
          <div className="px-5 py-4 flex flex-col gap-4">
            {NAV_ITEMS.map((link) => {
              if (link.type === "dropdown") return <MobileResources key={link.key} label={link.label || t.nav[link.key]} light={light} onNavigate={() => setOpen(false)} />;
              if (link.type === "route")
                return (
                  <Link key={link.key} to={link.to} onClick={() => setOpen(false)} className={linkCls}>
                    {link.label || t.nav[link.key]}
                  </Link>
                );
              return (
                <a key={link.key} href={lp(`/${link.href}`)} onClick={() => setOpen(false)} className={linkCls}>
                  {link.label || t.nav[link.key]}
                </a>
              );
            })}
            <Link to="/solicitar-demo" onClick={() => setOpen(false)} data-testid="nav-mobile-demo-button" className={`${ctaCls} text-center`}>
              {t.nav.bookDemo}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
